import React, { useMemo } from 'react'
import { Checkbox, Form, InputNumber, Radio, Spin, Switch, Table } from 'antd'
import { useRequest } from 'ahooks'
import Button from '@/components/button/button'
import $msg from '@/utils/message'
import Auth from '@/views/main/components/auth/auth'
import { loadMemberSettingConfig, updateMemberDiscount } from '../api'
import SettingPage, {
    MONTH_OPTIONS,
    SCENE_OPTIONS,
    SettingActions,
    SettingGrid,
    SettingInputNumber,
    SettingPanel,
    SettingPreview,
    SettingTableContainer,
    WEEK_OPTIONS,
    statusToBool,
} from './setting-page'
import { MemberDiscountFormValues, validateMemberDiscountLevels } from './member-discount-utils'
import { useMemberSettingAccess } from './setting-access'

const toDiscountRate = (value?: number | null) => {
    if (value === undefined || value === null) return null

    return Number(value)
}

const MemberDiscount: React.FC = () => {
    const [form] = Form.useForm<MemberDiscountFormValues>()
    const access = useMemberSettingAccess()

    const loadReq = useRequest(() => loadMemberSettingConfig('/activity/getmemberdiscountactivity'), {
        onSuccess: res => {
            const activity = res?.activity || {}
            form.setFieldsValue({
                enable: statusToBool(activity.status),
                frequency: activity.frequency || 0,
                weekdays: activity.weekdays || [],
                monthdays: activity.monthdays || [],
                has_balance: !!activity.has_balance,
                scenes: activity.scenes || [],
                levels: (activity.level_discount_list || []).map((item: any) => ({
                    member_level_id: item.member_level?.id || null,
                    level: item.member_level?.level ?? null,
                    name: item.member_level?.name || '付费会员',
                    discount_rate: item.discount_rate === null ? null : Number(item.discount_rate || 1),
                    origin_discount_rate: item.discount_rate === null ? null : Number(item.discount_rate || 1),
                })),
            })
        },
        onError: e => $msg('error', e.message || '获取会员折扣失败'),
    })

    const saveReq = useRequest(
        async (values: MemberDiscountFormValues) => {
            const baseParams = {
                enable: values.enable,
                ...(values.frequency === 0 ? { weekdays: values.weekdays || [] } : { monthdays: values.monthdays || [] }),
                has_balance: values.has_balance,
                scenes: values.scenes || [],
            }
            await updateMemberDiscount(baseParams)
            const changedLevels = (values.levels || []).filter(
                level => toDiscountRate(level.discount_rate) !== toDiscountRate(level.origin_discount_rate),
            )
            for (const level of changedLevels) {
                await updateMemberDiscount({
                    ...baseParams,
                    discount_level: {
                        member_level_id: level.member_level_id ?? null,
                        level: level.level ?? null,
                        discount_rate: toDiscountRate(level.discount_rate),
                    },
                })
            }
        },
        {
            manual: true,
            onSuccess: () => {
                $msg('success', '保存成功')
                loadReq.refresh()
            },
            onError: e => {
                $msg('error', `${e.message || '保存会员折扣失败'}，请核对已保存的设置`)
                loadReq.refresh()
            },
        },
    )

    const columns = useMemo(
        () => [
            {
                title: '会员等级',
                dataIndex: 'name',
                render: (value: string, __: any, index: number) => (
                    <>
                        <Form.Item name={['levels', index, 'member_level_id']} hidden>
                            <InputNumber />
                        </Form.Item>
                        <Form.Item name={['levels', index, 'level']} hidden>
                            <InputNumber />
                        </Form.Item>
                        <Form.Item name={['levels', index, 'origin_discount_rate']} hidden>
                            <InputNumber />
                        </Form.Item>
                        {value}
                    </>
                ),
            },
            {
                title: '折扣',
                render: (_: any, __: any, index: number) => (
                    <Form.Item name={['levels', index, 'discount_rate']} noStyle>
                        <SettingInputNumber
                            min={0.001}
                            max={0.999}
                            precision={3}
                            addonAfter="折扣率"
                            placeholder="不设置"
                        />
                    </Form.Item>
                ),
            },
        ],
        [],
    )

    const onSubmit = async () => {
        const values = await form.validateFields()
        if (values.enable) {
            const activeDays = values.frequency === 1 ? values.monthdays || [] : values.weekdays || []
            if (!activeDays.length) {
                $msg('warning', '请选择会员折扣生效日期')

                return
            }
            if (!(values.scenes || []).length) {
                $msg('warning', '请选择会员折扣适用场景')

                return
            }
            const discountLevelError = validateMemberDiscountLevels(values.levels || [])
            if (discountLevelError) {
                $msg('warning', discountLevelError)

                return
            }
        }
        saveReq.run(values)
    }

    return (
        <SettingPage title="会员折扣" readOnly={access.readOnly}>
            <Spin spinning={loadReq.loading}>
                <Form form={form} layout="vertical">
                    <SettingGrid>
                        <SettingPanel title="活动规则" desc="设置会员折扣的适用日期、场景和支付限制。">
                            <Form.Item noStyle shouldUpdate>
                                {() => (
                                    <SettingPreview
                                        items={[
                                            {
                                                label: '活动状态',
                                                value: form.getFieldValue('enable') ? '已开启' : '已关闭',
                                            },
                                            {
                                                label: '适用场景',
                                                value:
                                                    (form.getFieldValue('scenes') || [])
                                                        .map((scene: number) => (scene === 0 ? '收银台' : '商城'))
                                                        .join('、') || '--',
                                            },
                                        ]}
                                    />
                                )}
                            </Form.Item>
                            <Form.Item name="enable" label="活动状态" valuePropName="checked">
                                <Switch checkedChildren="开启" unCheckedChildren="关闭" />
                            </Form.Item>
                            <Form.Item name="frequency" label="生效周期">
                                <Radio.Group>
                                    <Radio value={0}>每周</Radio>
                                    <Radio value={1}>每月</Radio>
                                </Radio.Group>
                            </Form.Item>
                            <Form.Item noStyle shouldUpdate={(prev, next) => prev.frequency !== next.frequency}>
                                {() =>
                                    form.getFieldValue('frequency') === 1 ? (
                                        <Form.Item name="monthdays" label="每月生效日期">
                                            <Checkbox.Group options={MONTH_OPTIONS} />
                                        </Form.Item>
                                    ) : (
                                        <Form.Item name="weekdays" label="每周生效日期">
                                            <Checkbox.Group options={WEEK_OPTIONS} />
                                        </Form.Item>
                                    )
                                }
                            </Form.Item>
                            <Form.Item name="scenes" label="适用场景">
                                <Checkbox.Group options={SCENE_OPTIONS} />
                            </Form.Item>
                            <Form.Item name="has_balance" label="仅有余额会员可用" valuePropName="checked">
                                <Switch checkedChildren="是" unCheckedChildren="否" />
                            </Form.Item>
                        </SettingPanel>
                        <SettingPanel title="等级折扣" desc="不填表示不设置，0.88 表示 8.8 折。">
                            <Form.Item noStyle shouldUpdate>
                                {() => (
                                    <SettingTableContainer>
                                        <Table
                                            bordered
                                            rowKey={(_, index) => String(index)}
                                            columns={columns}
                                            dataSource={form.getFieldValue('levels') || []}
                                            pagination={false}
                                        />
                                    </SettingTableContainer>
                                )}
                            </Form.Item>
                            <SettingActions>
                                <Auth perssion={20} onClick={onSubmit}>
                                    <Button types="default" loading={saveReq.loading} disabled={saveReq.loading}>
                                        保存
                                    </Button>
                                </Auth>
                            </SettingActions>
                        </SettingPanel>
                    </SettingGrid>
                </Form>
            </Spin>
        </SettingPage>
    )
}

export default MemberDiscount
